import { Trash2, Users } from 'lucide-react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { useIsAdmin } from '@/lib/auth'
import { useSaveClientMembers } from '@/lib/clients-api'
import { usePersons } from '@/lib/settings-api'
import type { ClientDetail } from '@/lib/clients-types'
import { cn } from '@/lib/utils'
import { PERSON_NONE } from './shared'

export function MembersCard({ client }: { client: ClientDetail }) {
  const isAdmin = useIsAdmin()
  const personsQuery = usePersons()
  const saveMembers = useSaveClientMembers(client.id)
  const memberIds = client.members.map((m) => m.id)
  const addable = (personsQuery.data ?? []).filter((p) => p.active && !memberIds.includes(p.id))

  return (
    <Card>
      <CardHeader
        title="Toegang"
        subtitle="Wie werkt er voor deze klant. Een koppeling geldt voor alle projecten van de klant, ook nieuwe."
      />
      <div className="mt-4 space-y-3">
        {client.members.length === 0 ? (
          <p className="text-sm text-ink-faint">Nog niemand gekoppeld.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {client.members.map((member) => (
              <span
                key={member.id}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-pill bg-fill-2 py-1 pl-2.5 text-xs text-ink-soft',
                  isAdmin ? 'pr-1.5' : 'pr-2.5',
                  !member.active && 'opacity-60',
                )}
              >
                <Users className="h-3 w-3 text-ink-faint" />
                {member.displayName}
                <span className="text-ink-faint">{member.roleLabel}</span>
                {isAdmin ? (
                  <button
                    type="button"
                    onClick={() => saveMembers.mutate(memberIds.filter((id) => id !== member.id))}
                    disabled={saveMembers.isPending}
                    aria-label={`${member.displayName} loskoppelen`}
                    className="text-ink-faint hover:text-ink"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        )}
        {isAdmin && addable.length > 0 ? (
          <div className="max-w-xs">
            <Select
              value={PERSON_NONE}
              onValueChange={(id) => id !== PERSON_NONE && saveMembers.mutate([...memberIds, id])}
              disabled={saveMembers.isPending}
              options={[{ value: PERSON_NONE, label: 'Persoon koppelen…' }, ...addable.map((p) => ({ value: p.id, label: p.displayName }))]}
            />
          </div>
        ) : null}
        <p className="text-xs text-ink-faint">
          Beheerder en Partner zien altijd alle klanten; voor hen is de koppeling alleen een aantekening. Voor Onderaannemer en Externe boeker bepaalt hij wat ze zien en waarop ze mogen boeken.
        </p>
      </div>
    </Card>
  )
}
